import { useMemo, useState } from "react";
import { SiteFooter, SiteNav } from "@/components/media/SiteNav";
import { Eyebrow } from "@/components/media/Sections";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const INPUTS = [
  ["attendance", "PROJECTED EVENT ATTENDANCE", 8000],
  ["conversion", "ESTIMATED CONVERSION RATE (%)", 4],
  ["cupPrice", "CUP PRICE ($)", 9],
  ["productCost", "PRODUCT COST PER CUP ($)", 2.25],
  ["vendorFee", "VENDOR FEE ($)", 350],
  ["laborHours", "TOTAL LABOR HOURS", 16],
  ["laborRate", "LABOR RATE ($/HR)", 18],
  ["otherCosts", "OTHER COSTS — FUEL, ICE, PERMITS ($)", 120],
];

const money = (n) => {
  const v = Number.isFinite(n) ? n : 0;
  const abs = Math.abs(v).toLocaleString("en-US", { maximumFractionDigits: 0 });
  return `${v < 0 ? "-" : ""}$${abs}`;
};

const Row = ({ label, value, strong, accent }) => (
  <div
    data-testid={`calc-row-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    className="flex items-baseline justify-between gap-4 border-b border-border/60 py-3 last:border-0"
  >
    <span className={`text-sm ${strong ? "text-white" : "text-muted-foreground"}`}>{label}</span>
    <span
      className={`display text-2xl ${
        accent ? "text-primary" : strong ? "text-white" : "text-muted-foreground"
      }`}
    >
      {value}
    </span>
  </div>
);

export default function Calculator() {
  const [form, setForm] = useState(() =>
    Object.fromEntries(INPUTS.map(([k, , d]) => [k, String(d)]))
  );
  const n = (k) => parseFloat(form[k]) || 0;

  const result = useMemo(() => {
    const drinks = Math.round(n("attendance") * (n("conversion") / 100));
    const gross = drinks * n("cupPrice");
    const product = drinks * n("productCost");
    const labor = n("laborHours") * n("laborRate");
    const expenses = product + labor + n("vendorFee") + n("otherCosts");
    const profit = gross - expenses;
    const margin = n("cupPrice") - n("productCost");
    const fixed = labor + n("vendorFee") + n("otherCosts");
    const breakEven = margin > 0 ? Math.ceil(fixed / margin) : 0;
    return {
      drinks, gross, product, labor, expenses, profit, breakEven,
      inventory: Math.ceil(drinks * 1.2),
    };
  }, [form]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="calculator-page">
      <SiteNav />
      <div className="mx-auto max-w-6xl px-5 py-14 md:px-10 md:py-20">
        <Eyebrow>FREE TOOL</Eyebrow>
        <h1 className="neon mt-3 text-4xl leading-tight sm:text-5xl">
          Know your numbers <span className="text-primary">before you show up.</span>
        </h1>

        <div className="mt-12 grid gap-10 lg:grid-cols-2">
          <div className="space-y-4 border border-border bg-[#141414] p-6 md:p-8">
            <h2 className="display text-2xl uppercase">The event</h2>
            {INPUTS.map(([key, label]) => (
              <div key={key}>
                <Label className="mono text-[10px] tracking-widest text-muted-foreground">
                  {label}
                </Label>
                <Input
                  data-testid={`calc-input-${key}`}
                  type="number"
                  step="0.01"
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="mt-1 rounded-sm bg-[#0A0A0A]"
                />
              </div>
            ))}
          </div>

          <div className="border border-primary/50 bg-[#191603] p-6 md:p-8" data-testid="calc-results">
            <h2 className="display text-2xl uppercase">The verdict</h2>
            <div className="mt-4">
              <Row label="Projected drink sales" value={result.drinks.toLocaleString()} />
              <Row label="Estimated gross revenue" value={money(result.gross)} />
              <Row label="Product costs" value={money(result.product)} />
              <Row label="Labor" value={money(result.labor)} />
              <Row label="Estimated expenses" value={money(result.expenses)} strong />
              <Row label="Estimated event profit" value={money(result.profit)} accent strong />
              <Row label="Break-even cups" value={result.breakEven.toLocaleString()} />
              <Row label="Recommended inventory target" value={`${result.inventory.toLocaleString()} cups`} />
            </div>
            <p
              data-testid="calc-callout"
              className={`display mt-8 text-2xl leading-tight ${
                result.profit > 0 ? "text-primary" : "text-destructive"
              }`}
            >
              {result.profit > 0
                ? "This event pencils out. Go get the spot."
                : "At these numbers this event loses money. Negotiate the fee or walk."}
            </p>
            <p className="mono mt-4 text-[10px] leading-relaxed text-muted-foreground">
              ESTIMATES ONLY. WEATHER, LINE SPEED AND EVENT QUALITY MOVE THESE NUMBERS.
            </p>
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
